import { Link, Outlet } from "@tanstack/react-router";
import { useState } from "react";
import { ReactComponent as DiscussionsIcon } from "assets/img/icons//sidebar/discussions.svg";
import { ReactComponent as BenchMarkIcon } from "assets/img/icons/sidebar/benchmark.svg";
import { ReactComponent as BudgetIcon } from "assets/img/icons/sidebar/budget.svg";
import { ReactComponent as ChevronDoubleLeftIcon } from "assets/img/icons/sidebar/chevron-double-left.svg";
import { ReactComponent as ChevronDoubleRightIcon } from "assets/img/icons/sidebar/chevron-double-right.svg";
import { ReactComponent as ChartsIcon } from "assets/img/icons/sidebar/db.svg";
import { ReactComponent as DocumentsIcon } from "assets/img/icons/sidebar/documents.svg";
import { ReactComponent as EventsIcon } from "assets/img/icons/sidebar/events.svg";
import { ReactComponent as FavoritesIcon } from "assets/img/icons/sidebar/favorite-articles.svg";
import { ReactComponent as LiveIcon } from "assets/img/icons/sidebar/live.svg";
import { ReactComponent as PrivPortalIcon } from "assets/img/icons/sidebar/priv-portal.svg";
import { ReactComponent as SocPortalIcon } from "assets/img/icons/sidebar/soc-portal.svg";
import { ReactComponent as LogoIcon } from "assets/img/logo-icon.svg";
import { ReactComponent as CompassLogo } from "assets/img/logo.svg";
import { ReactComponent as LogoutIcon } from "assets/img/nav-icons/logout.svg";
import { SideAvatar } from "components/elements/SideAvatar";
import { useAllOrganizations } from "hooks/useAllOrganizations";
import { useIsUserLoggedIn } from "hooks/useIsUserLoggedIn";
import { usePageDetector } from "hooks/usePageDetector";
import { trpc } from "lib/trpc";
import { Header } from "./Header";
import { NavigationLink, NavigationLinks } from "./NavigationLinks";
import { ResourceDropdown } from "./ResourceDropdown";
import { SocialDropdown } from "./SocialDropdown";

const portalLinks: NavigationLink[] = [
    { to: "/discussions", name: "Private Discussions", Icon: DiscussionsIcon },
    { to: "/benchmark", name: "Benchmark", Icon: BenchMarkIcon },
    { to: "/budget", name: "Budget", Icon: BudgetIcon },
    { to: "/documents", name: "Documents", Icon: DocumentsIcon },
    { to: "/visualizations", name: "Visualizations", Icon: ChartsIcon },
    { to: "/private-events", name: "Private Events", Icon: EventsIcon },
    { to: "/favorites/private", name: "Favorites", Icon: FavoritesIcon },
];

const NoAccess = () => {
    return (
        <div className="flex h-full flex-col items-center justify-center">
            <h1 className="text-4xl font-bold text-[#424242]">
                Sorry, you don't have access to this page.
            </h1>
            {/*<p className="flex text-2xl">Sorry, you don’t have access to this page.</p>*/}
        </div>
    );
};

const DisabledAccount = () => {
    return (
        <div className="flex h-full flex-col items-center justify-center">
            <h1 className="text-4xl font-bold text-[#424242]">
                Sorry, your account has been disabled.
            </h1>
            {/*<p className="flex text-2xl">Sorry, you don’t have access to this page.</p>*/}
        </div>
    );
};

export const PortalLayout = () => {
    const isLoggedIn = useIsUserLoggedIn();
    const { data: user } = trpc.users.me.useQuery(undefined, { enabled: isLoggedIn });
    const { data: notifications } = trpc.notifications.latest.useQuery();
    const { isSocialPage, isPrivatePage, isLoginPage, isDiscussionPage } = usePageDetector();
    //const [resources] = trpc.resources.categories.useSuspenseQuery();
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

    // Prime the org list cache once on app start so every org picker reads
    // from cache (5min stale window). Compass-user-gated server-side.
    useAllOrganizations(user ?? undefined);

    const socialLinks: NavigationLink[] = [
        { to: "/events/list", name: "Events", Icon: EventsIcon },
        { to: "/live", name: "Live", Icon: LiveIcon },
    ];

    if (user && !user?.isSocialEnabled && !user?.isPrivateEnabled) {
        return <DisabledAccount />;
    }

    return (
        <div className="mx-auto flex w-full flex-row">
            {/* Sidebar */}
            {/* <div className="sticky top-0 z-20 h-[calc(100dvh+80px)] shrink-0 px-6 py-6"> */}
            <aside>
                <div className="sticky top-0 z-20 h-[calc(100dvh)] shrink-0 px-6 py-6">
                    {/* className={`bg-brand-polarice border-brand-shine flex h-full flex-col overflow-hidden rounded-2xl border transition-all ${ */}
                    <div
                        className={`bg-brand-polarice border-brand-shine relative flex h-full flex-col overflow-hidden rounded-2xl border transition-all ${
                            isSidebarCollapsed ? "w-fit px-3 py-6" : "w-[280px] p-4"
                        }`}
                    >
                        {/* Header Section (logo, user, portal switch) */}
                        <div className="shrink-0">
                            {/* Logo Section */}
                            <div
                                className={`flex ${
                                    isSidebarCollapsed
                                        ? "flex-col gap-4"
                                        : "flex-row justify-between"
                                } items-center pb-3`}
                            >
                                {isSidebarCollapsed && (
                                    <button
                                        className="cursor-pointer"
                                        onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                                    >
                                        <ChevronDoubleRightIcon className="h-6 w-6" />
                                    </button>
                                )}
                                <div className="flex items-center justify-center">
                                    <Link
                                        to="/"
                                        style={{ textDecoration: "none", color: "inherit" }}
                                    >
                                        {isSidebarCollapsed ? (
                                            <LogoIcon className="h-12 w-12" />
                                        ) : (
                                            <CompassLogo className="h-12 w-auto" />
                                        )}
                                    </Link>
                                </div>
                                {!isSidebarCollapsed && (
                                    <button
                                        className="cursor-pointer"
                                        onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
                                    >
                                        <ChevronDoubleLeftIcon className="h-6 w-6" />
                                    </button>
                                )}
                            </div>
                            {/* Current user */}
                            <div
                                className={`flex items-center ${
                                    isSidebarCollapsed ? "justify-center" : ""
                                } gap-3 pt-2`}
                            >
                                <div
                                    className={`${
                                        isSidebarCollapsed ? "h-12 w-12" : "h-auto w-[80px]"
                                    } shrink-0`}
                                >
                                    <SideAvatar
                                        userId={user?.id}
                                        photoUrl={user?.avatarUrl}
                                        name={user?.name}
                                    />
                                </div>
                                {!isSidebarCollapsed && (
                                    <div className="flex min-w-0 flex-col gap-1">
                                        <div className="truncate text-xl font-semibold text-gray-800">
                                            {user?.name}
                                        </div>
                                        <div className="text-base text-gray-500">
                                            {user?.organizationName ?? user?.email}
                                        </div>
                                    </div>
                                )}
                            </div>
                            {/* Section Switch */}
                            {!isSidebarCollapsed ? (
                                <div
                                    className={`mt-8 flex rounded-md border border-blue-400 p-1 ${
                                        isSocialPage ? "bg-brand-atlantic" : "bg-brand-atlantic"
                                    } ${user?.isSocialEnabled ? "justify-between space-x-1" : ""}`}
                                >
                                    {user?.isPrivateEnabled && (
                                        <Link
                                            to="/discussions"
                                            className={`${
                                                user?.isPrivateEnabled ? "w-1/2" : "w-full"
                                            } text-md rounded-md py-2 text-center font-normal no-underline transition-all duration-300 ease-in-out ${
                                                isPrivatePage
                                                    ? "brand-black5 bg-gray-100 focus:outline-none focus:ring-0"
                                                    : "bg-transparent text-black hover:bg-blue-400"
                                            }`}
                                            style={{
                                                textDecoration: "none",
                                                color: "inherit",
                                            }}
                                        >
                                            Private portal
                                        </Link>
                                    )}
                                    {user?.isSocialEnabled && (
                                        <Link
                                            to="/social"
                                            className={`text-md w-1/2 rounded-md py-2 text-center font-normal no-underline transition-all duration-300 ease-in-out ${
                                                isSocialPage
                                                    ? "brand-black5 bg-gray-100 focus:outline-none focus:ring-0"
                                                    : "bg-transparent text-black hover:bg-blue-400"
                                            }`}
                                            style={{
                                                textDecoration: "none",
                                                color: "inherit",
                                            }}
                                        >
                                            Social portal
                                        </Link>
                                    )}
                                </div>
                            ) : (
                                <div className="flex w-full justify-center">
                                    {isSocialPage ? (
                                        <SocPortalIcon className="mt-12 h-12 w-10" />
                                    ) : (
                                        <PrivPortalIcon className="mt-12 h-12 w-10" />
                                    )}
                                </div>
                            )}
                        </div>
                        {/* Scrollable Navigation Section */}
                        {/* <div className={`${isSidebarCollapsed ? "mt-2" : "mt-7"} min-h-0`}> */}
                        <div
                            className={`${isSidebarCollapsed ? "mt-2" : "mt-7"} scrollbar-thin scrollbar-thumb-brand-black5 scrollbar-track-transparent flex-1 overflow-y-auto`}
                            style={{ scrollbarGutter: "__stable" }}
                        >
                            <div className="__space-y-2 pb-16">
                                {isSocialPage && user?.isSocialEnabled && (
                                    <>
                                        <SocialDropdown
                                            isCollapsed={isSidebarCollapsed}
                                            isSocialPage={isSocialPage}
                                        />
                                        <ResourceDropdown isCollapsed={isSidebarCollapsed} />
                                    </>
                                )}
                                <NavigationLinks
                                    links={
                                        isSocialPage && user?.isSocialEnabled
                                            ? socialLinks
                                            : portalLinks
                                    }
                                    isCollapsed={isSidebarCollapsed}
                                />
                            </div>
                        </div>
                        {/* Log Out */}
                        {/* <div className="mt-0 flex shrink-0 justify-center"> */}
                        <div className="bg-brand-polarice/70 absolute bottom-0 left-0 right-0 z-30 flex justify-center py-2.5 backdrop-blur-md">
                            <Link
                                to="/logout"
                                style={{ textDecoration: "none", color: "inherit" }}
                                className="text-md flex items-center font-normal text-gray-700 transition-colors duration-200 hover:text-red-500"
                            >
                                {!isSidebarCollapsed && <span className="mr-2">Log Out</span>}
                                <LogoutIcon
                                    className={`${
                                        isSidebarCollapsed ? "h-7 w-7" : "h-4 w-4"
                                    } cursor-pointer text-gray-500 group-hover:text-red-500`}
                                />
                            </Link>
                        </div>
                    </div>
                </div>
            </aside>
            {/* Main Content */}
            <div id="scrollable-content" className="flex h-screen flex-1 flex-col overflow-auto">
                <div className="mr-6">
                    <Header
                        isLoginPage={isLoginPage}
                        notifications={notifications ?? []}
                        isSocialPage={isSocialPage}
                        isLoggedIn={isLoggedIn}
                        user={user}
                    />
                    {user &&
                    ((isSocialPage && !user.isSocialEnabled) ||
                        (isPrivatePage && !user.isPrivateEnabled)) ? (
                        <NoAccess />
                    ) : (
                        <main>
                            <Outlet />
                        </main>
                    )}
                </div>
            </div>
        </div>
    );
};
